/**
 * The Findings Walkthrough's columns and its description-line choices.
 *
 * Deliberately a mirror of WALKTHROUGH_COLUMN_KEYS / WALKTHROUGH_DEFAULT_COLUMNS
 * and walkthroughColumns in backend/utils/report-helpers.js, as
 * riskRatingDefaults.js and tableLabelDefaults.js already mirror theirs.
 *
 * KEYS and DEFAULTS are different lists on purpose. 'exploitable' is a column
 * the renderers know but no template gets by default, so adding it changed no
 * template that already existed and adds no column to one created later -- it
 * appears when an author ticks it and not before.
 *
 * KEYS is also the order columns print in, which is what lets a column ticked
 * later land in its proper place rather than on the end of the list.
 */

export const WALKTHROUGH_COLUMN_KEYS = ['uid', 'finding', 'severity', 'cvss', 'exploitable', 'status'];
export const WALKTHROUGH_DEFAULT_COLUMNS = ['finding', 'severity', 'cvss', 'status'];

/** The house heading for each column, and the label the editor shows. */
export const WALKTHROUGH_COLUMN_LABELS = {
  uid: 'Finding ID', finding: 'Finding', severity: 'Severity', cvss: 'CVSS',
  exploitable: 'Exploitable', status: 'Status',
};

/** The config key each column's heading override lives under. */
export const WALKTHROUGH_LABEL_KEYS = {
  uid: 'labelUid', finding: 'labelFinding', severity: 'labelSeverity',
  cvss: 'labelCvss', exploitable: 'labelExploitable', status: 'labelStatus',
};

/**
 * The finding column's heading once the identifier has a column of its own.
 * 'Finding' beside 'Finding ID' reads as two names for one thing, and the
 * cell no longer holds the identifier, so what is left in it is the title.
 */
export const WALKTHROUGH_SPLIT_FINDING_LABEL = 'Title';

/** "Heading only": a headline and nothing under it. Not a field. */
export const WALKTHROUGH_DESCRIPTION_NONE = 'none';

export function walkthroughColumns(config) {
  const given = (config || {}).columns;
  const picked = Array.isArray(given) && given.length
    ? WALKTHROUGH_COLUMN_KEYS.filter(k => given.includes(k))
    : [];
  return picked.length ? picked : [...WALKTHROUGH_DEFAULT_COLUMNS];
}

/** Every column's heading, the template's where it set one. */
export function walkthroughLabels(config) {
  const cfg = config || {};
  const house = { ...WALKTHROUGH_COLUMN_LABELS };
  if (walkthroughColumns(cfg).includes('uid')) house.finding = WALKTHROUGH_SPLIT_FINDING_LABEL;
  const out = {};
  Object.keys(house).forEach(k => {
    const given = cfg[WALKTHROUGH_LABEL_KEYS[k]];
    // A stored heading equal to the house one counts as unset: the TSR
    // preset wrote labelFinding: 'Finding' into every walkthrough it
    // created. See the backend copy for the full reasoning.
    const isHouse = given === WALKTHROUGH_COLUMN_LABELS[k];
    out[k] = (typeof given === 'string' && !isHouse) ? given : house[k];
  });
  return out;
}

/**
 * Whether the finding cell still prepends the identifier to the title.
 *
 * It does not once the identifier has a column: printing it in both places
 * is what the Finding ID column exists to stop. The showUid checkbox is
 * left alone rather than cleared, so unticking the column puts the inline
 * identifier back exactly as the author last had it.
 */
export function walkthroughInlineUid(config) {
  const cfg = config || {};
  if (walkthroughColumns(cfg).includes('uid')) return false;
  return cfg.showUid !== false;
}

export function walkthroughDescriptionField(config) {
  const given = (config || {}).descriptionField;
  if (given === WALKTHROUGH_DESCRIPTION_NONE) return null;
  return ['details', 'observation', 'recommendation'].includes(given) ? given : 'details';
}

export function walkthroughShowsDescription(config) {
  return (config || {}).showDescription !== false
    && walkthroughDescriptionField(config) !== null;
}
